export function formatCardDuration(durationHours: number | null): string | null {
  if (durationHours === null) {
    return null;
  }

  if (durationHours < 1) {
    const minutes = Math.round(durationHours * 60);
    return `${minutes}m`;
  }

  if (Number.isInteger(durationHours)) {
    return `${durationHours}h`;
  }

  return `${durationHours}h`;
}

export function formatCountdown(targetDate: string): string {
  const delta = new Date(targetDate).getTime() - Date.now();

  if (delta <= 0) {
    return "00:00:00";
  }

  const totalSeconds = Math.floor(delta / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return [hours, minutes, seconds]
    .map((value) => value.toString().padStart(2, "0"))
    .join(":");
}

export function formatRelativeTime(dateString: string): string {
  const deltaMs = Date.now() - new Date(dateString).getTime();
  const deltaMinutes = Math.max(1, Math.floor(deltaMs / 60000));

  if (deltaMinutes < 60) {
    return `${deltaMinutes}m ago`;
  }

  const deltaHours = Math.floor(deltaMinutes / 60);
  if (deltaHours < 24) {
    return `${deltaHours}h ago`;
  }

  const deltaDays = Math.floor(deltaHours / 24);
  return `${deltaDays}d ago`;
}

/** Humanizes a positive duration in ms — e.g. "3d 4h", "12m 35s", "45s". */
export function humanizeDurationMs(deltaMs: number): string {
  if (!Number.isFinite(deltaMs) || deltaMs <= 0) {
    return "0s";
  }
  const totalSeconds = Math.floor(deltaMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) {
    return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  }
  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
  if (minutes > 0) {
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  }
  return `${seconds}s`;
}

/**
 * Humanized relative remaining time vs. now.
 *
 * Returns null when `dateString` is missing/invalid; "expired" when in the past.
 */
export function humanizeUntil(dateString?: string | null): string | null {
  if (!dateString) {
    return null;
  }
  const target = new Date(dateString).getTime();
  if (!Number.isFinite(target)) {
    return null;
  }
  const delta = target - Date.now();
  if (delta <= 0) {
    return "expired";
  }
  return humanizeDurationMs(delta);
}

export function formatEffectRemaining(dateString?: string | null): string | null {
  if (!dateString) {
    return null;
  }

  const delta = new Date(dateString).getTime() - Date.now();
  if (delta <= 0) {
    return "expired";
  }

  const totalSeconds = Math.ceil(delta / 1000);
  if (totalSeconds < 60) {
    return `${totalSeconds}s left`;
  }

  const totalMinutes = Math.ceil(delta / 60000);
  if (totalMinutes < 60) {
    return `${totalMinutes}m left`;
  }

  const totalHours = Math.ceil(totalMinutes / 60);
  if (totalHours < 24) {
    return `${totalHours}h left`;
  }

  const totalDays = Math.ceil(totalHours / 24);
  return `${totalDays}d left`;
}

export function isSameUtcDay(leftDate: string, rightDate: string): boolean {
  return new Date(leftDate).toISOString().slice(0, 10) === new Date(rightDate).toISOString().slice(0, 10);
}

/** High-impact cards that should require a confirmation modal before play. */
export const HIGH_IMPACT_CARD_SLUGS = new Set<string>([
  "reverse",
  "fog-of-war",
  "lockdown",
]);

export function isHighImpactCard(card: {
  slug?: string;
  rarity?: string;
} | null | undefined): boolean {
  if (!card) return false;
  if (card.rarity === "legendary") return true;
  return Boolean(card.slug && HIGH_IMPACT_CARD_SLUGS.has(card.slug));
}

/**
 * Cards whose play is announced to the room by name. Mirrors the backend's
 * `HIGH_IMPACT_AUDIT_SLUGS` (`modules/cards/services/runtime/cardPlayNotifier.ts`),
 * which is the one gate on the audience fanout — deliberately narrower than the
 * confirm set above, and narrower than "epic or legendary", because the cards
 * whose whole mechanic is concealment must stay quiet.
 */
export const ANNOUNCED_CARD_SLUGS = new Set<string>([
  "reverse",
  "fog-of-war",
  "lockdown",
  "sprint-lead",
  "jinx",
  "radio-silence",
]);

/** True when playing this card tells everyone else, so the toast can say so. */
export function isAnnouncedCard(
  card: { slug?: string } | null | undefined,
): boolean {
  return Boolean(card?.slug && ANNOUNCED_CARD_SLUGS.has(card.slug));
}

export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (
    error &&
    typeof error === "object" &&
    "data" in error &&
    error.data &&
    typeof error.data === "object" &&
    "message" in error.data &&
    typeof error.data.message === "string"
  ) {
    return error.data.message;
  }

  return fallback;
}
